import api from './api'
import type { BackgroundJob } from './jobs'

export type GcpAnalyzerSectionKey =
  | 'billing'
  | 'bigquery'
  | 'cloud_run'
  | 'cloud_sql'
  | 'gcs'
  | 'compute'
  | 'egress'
  | 'misc'

export interface GcpSectionError {
  error: string
  missing_role_hint?: string | null
}

export interface GcpAnalysisMeta {
  project_id: string
  analysis_months: number
  metrics_days: number
  analysis_date: string
}

export interface GcpRecurringQueryGroupRunSample {
  creation_time?: string
  total_bytes_processed?: number
  estimated_cost_usd?: number
  job_id?: string
}

export interface GcpWastePattern {
  pattern: 'metadata_scan' | 'full_rebuild_ctas' | 'uncached_repeat' | 'unpartitioned_hotspot' | string
  explanation_plain: string
  estimated_savings_usd_monthly: number
  suggested_fix: string
  fix_command_or_sql: string
}

export interface GcpRecurringQueryGroup {
  fingerprint: string
  run_count: number
  median_interval_seconds?: number | null
  cadence_label: string
  statement_type?: string
  avg_bytes_processed?: number
  avg_tb_processed?: number
  avg_cost_per_run?: number
  runs_per_30d?: number
  estimated_monthly_cost: number
  trigger: string
  trigger_label: string
  user_email?: string
  destination_table?: string
  top_referenced_tables?: string[]
  cache_hit_pct?: number
  query_preview?: string
  full_query_sample?: string
  run_samples?: GcpRecurringQueryGroupRunSample[]
  waste_patterns?: GcpWastePattern[]
}

export type GcpSectionData = Record<string, unknown> | GcpSectionError

export interface GcpAnalysisResult {
  _meta: GcpAnalysisMeta
  sections: Partial<Record<GcpAnalyzerSectionKey, GcpSectionData>>
}

export interface GcpQuickWin {
  title: string
  command: string
  estimated_savings_usd_monthly: number
  effort: string
  related_guardrail?: string
  related_change?: string
}

export interface GcpRecommendation {
  priority: number
  service: string
  resource?: string
  issue: string
  recommendation: string
  estimated_savings_usd_monthly: number
  estimated_savings?: string
  risk: 'low' | 'medium' | 'high'
  effort: string
  impact?: string
  implementation: string
  verification: string
  data_citation: string
  related_guardrail?: string
  related_change?: string
}

export interface GcpAdditionalDataNeeded {
  data_type: string
  reason: string
  how_to_get: string
}

export interface NewSkuRow {
  service: string
  sku: string
  first_seen_month: string
  monthly_cost_usd: number
}

export interface LevelShiftRow {
  service: string
  onset_date: string
  recent_avg_daily_usd: number
  prior_avg_daily_usd: number
  ratio: number
  delta_daily_usd: number
  run_rate_delta_monthly_usd: number
}

export interface WhatChangedPayload {
  new_skus: NewSkuRow[]
  level_shifts: LevelShiftRow[]
}

export interface RunRateRow {
  service: string
  sku?: string
  avg_daily_usd: number
  run_rate_monthly_usd: number
  window_start: string
  window_end: string
  days_observed: number
}

export interface RunRatesPayload {
  by_service: RunRateRow[]
  by_service_sku: RunRateRow[]
  window_days: number
  lag_days_excluded: number
  as_of: string
}

export type SpendBridgePartition = 'new_sku' | 'grown' | 'shrunk' | 'disappeared'

export interface SpendBridgeRow {
  service: string
  prev_month_usd: number
  current_run_rate_usd: number
  delta_usd: number
  partition: SpendBridgePartition
  onset_date: string | null
}

export interface SpendBridge {
  prev_complete_month: string | null
  prev_total_usd: number
  current_run_rate_total_usd: number
  total_delta_usd: number
  rows: SpendBridgeRow[]
}

export interface CurrentMonthToDate {
  month: string
  total_usd: number
}

export interface EgressDailyPoint {
  date: string
  sent_gb: number
}

export interface SqlInstanceEgress {
  instance: string
  daily: EgressDailyPoint[]
  total_gb: number
  est_monthly_cost: number
  window_days: number
}

export type ConnectionPathLabel = 'public_ip_only' | 'private_ip' | 'unknown'

export interface ConnectionPath {
  instance: string
  ipv4_enabled: boolean
  private_network: string | null
  authorized_networks_count: number
  path_label: ConnectionPathLabel
}

export type ClientPathVerdict =
  | 'socket_public'
  | 'tcp_public'
  | 'tcp_private'
  | 'unknown_secret'

export interface ClientPath {
  client: string
  client_kind: 'service' | 'job' | string
  instance: string | null
  path_verdict: ClientPathVerdict
  path_label: string
  evidence: string
  billed: boolean | null
}

export interface EgressCostByServiceSku {
  month: string
  service: string
  sku: string
  net_cost: number
  usage_amount?: number
  usage_unit?: string
}

export interface EgressCostByResource {
  service: string
  sku: string
  resource_name: string
  net_cost: number
  usage_amount?: number
  usage_unit?: string
}

export interface EgressSectionData {
  analysis_months?: number
  metrics_days?: number
  costs_by_service_sku?: EgressCostByServiceSku[]
  costs_by_resource?: EgressCostByResource[]
  sql_instance_egress?: SqlInstanceEgress[]
  connection_paths?: ConnectionPath[]
  client_paths?: ClientPath[]
  recommendations?: unknown[]
  headline_run_rate_usd_monthly?: number
}

export interface GcpMiscServiceSku {
  sku: string
  net_cost: number
  usage_amount?: number
  usage_unit?: string
}

export interface GcpMiscServiceRow {
  service: string
  run_rate_monthly_usd: number
  mom_change_usd?: number
  skus: GcpMiscServiceSku[]
}

export interface GcpTaxItem {
  service: string
  label: string
  run_rate_monthly_usd?: number
  monthly_cost_usd?: number
}

export interface GcpMiscSectionData {
  analysis_months?: number
  services?: GcpMiscServiceRow[]
  tax_items?: GcpTaxItem[]
  tax_and_credits?: {
    tax_items?: GcpTaxItem[]
    excluded_from_optimization_usd?: number
  }
  excluded_from_optimization_usd?: number
  recommendations?: unknown[]
  headline_run_rate_usd_monthly?: number
  storage_intelligence_detected?: boolean
}

export interface GcpBillingSection {
  monthly_by_service?: unknown[]
  what_changed?: WhatChangedPayload
  run_rates?: RunRatesPayload
  spend_bridge?: SpendBridge
  latest_complete_month?: string | null
  current_month_to_date?: CurrentMonthToDate
  projected_current_month?: number | null
}

export interface CloudRunBillingModes {
  instance_based: number
  min_instance: number
  request_based: number
  jobs: number
  other: number
}

export interface CloudRunBillingModeByService {
  service: string
  modes: CloudRunBillingModes
  avg_cpu_utilization_pct: number | null
  cold_start_latency_ms: number | null
}

export interface CloudRunBillingModeChange {
  service: string
  first_seen_month: string
  sku: string
  monthly_cost_usd: number
}

export interface CloudRunSectionData {
  analysis_months?: number
  metrics_days?: number
  services?: unknown[]
  costs?: unknown[]
  billing_mode_by_service?: CloudRunBillingModeByService[]
  billing_mode_changes?: CloudRunBillingModeChange[]
  recommendations?: unknown[]
}

export interface CloudSqlInstanceRow {
  name: string
  tier?: string
  availability_type?: string
  data_disk_size_gb?: number
  ipv4_enabled?: boolean
  private_network?: string | null
  authorized_networks_count?: number
  cpu_utilization?: Record<string, unknown>
  memory_utilization?: Record<string, unknown>
  disk_utilization?: Record<string, unknown>
  connections?: {
    avg_connections?: number | null
    max_connections?: number | null
  }
  billed_monthly_usd?: number
  backup_enabled?: boolean
}

export interface CloudSqlSectionData {
  analysis_months?: number
  metrics_days?: number
  instances?: CloudSqlInstanceRow[]
  costs?: unknown[]
  recommendations?: unknown[]
}

export interface GcpRecommendationsResult {
  summary: string
  latest_monthly_total_usd: number
  total_potential_savings_usd_monthly: number
  quick_wins: GcpQuickWin[]
  recommendations: GcpRecommendation[]
  additional_data_needed: GcpAdditionalDataNeeded[]
  error?: string
}

export interface GcpWindowPreset {
  id: string
  label: string
  months: number
  metricsDays: number
}

export const WINDOW_PRESETS: readonly GcpWindowPreset[] = [
  { id: '30d', label: 'Last 30 days', months: 1, metricsDays: 30 },
  { id: '60d', label: 'Last 60 days', months: 2, metricsDays: 30 },
  { id: '90d', label: 'Last 90 days', months: 3, metricsDays: 30 },
  { id: '6m', label: 'Last 6 months', months: 6, metricsDays: 30 },
] as const

export interface StartGcpCostJobResponse {
  job_id: number
}

export async function startGcpCostAnalysis(
  months: number,
  metricsDays: number
): Promise<StartGcpCostJobResponse> {
  const response = await api.post<StartGcpCostJobResponse>('/gcp-costs/analyze', {
    months,
    metrics_days: metricsDays,
  })
  return response.data
}

export async function startGcpCostRecommendations(
  analysisJobId: number
): Promise<StartGcpCostJobResponse> {
  const response = await api.post<StartGcpCostJobResponse>('/gcp-costs/recommend', {
    analysis_job_id: analysisJobId,
  })
  return response.data
}

export async function fetchGcpAnalysisXlsx(
  jobId: number
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get<Blob>(`/gcp-costs/analysis-jobs/${jobId}/xlsx`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] as string | undefined
  let filename = `gcp-analysis-${jobId}.xlsx`
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition)
    if (match?.[1]) filename = match[1]
  }
  return { blob: response.data, filename }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isGcpSectionError(section: GcpSectionData | undefined): section is GcpSectionError {
  return isRecord(section) && typeof section.error === 'string'
}

export function getAnalysisResultFromJob(job: BackgroundJob): GcpAnalysisResult | null {
  const rd = job.result_data as unknown
  if (!isRecord(rd)) return null
  const meta = rd._meta
  const sections = rd.sections
  if (!isRecord(meta) || !isRecord(sections)) return null
  if (typeof meta.project_id !== 'string') return null
  return rd as unknown as GcpAnalysisResult
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function asRisk(value: unknown): GcpRecommendation['risk'] {
  return value === 'low' || value === 'medium' || value === 'high' ? value : 'medium'
}

export function getRecommendationsResultFromJob(
  job: BackgroundJob
): GcpRecommendationsResult | null {
  const rd = job.result_data as unknown
  if (!isRecord(rd)) return null
  if (typeof rd.summary !== 'string') return null

  const quickWins = Array.isArray(rd.quick_wins) ? rd.quick_wins : []
  const recs = Array.isArray(rd.recommendations) ? rd.recommendations : []
  const additional = Array.isArray(rd.additional_data_needed) ? rd.additional_data_needed : []

  return {
    summary: rd.summary,
    latest_monthly_total_usd: asNumber(rd.latest_monthly_total_usd),
    total_potential_savings_usd_monthly: asNumber(rd.total_potential_savings_usd_monthly),
    error: typeof rd.error === 'string' ? rd.error : undefined,
    quick_wins: quickWins.filter(isRecord).map((row) => ({
      title: String(row.title ?? ''),
      command: String(row.command ?? ''),
      estimated_savings_usd_monthly: asNumber(row.estimated_savings_usd_monthly),
      effort: String(row.effort ?? ''),
      related_guardrail:
        typeof row.related_guardrail === 'string' ? row.related_guardrail : undefined,
      related_change: typeof row.related_change === 'string' ? row.related_change : undefined,
    })),
    recommendations: recs.filter(isRecord).map((row) => ({
      priority: asNumber(row.priority),
      service: String(row.service ?? ''),
      resource: typeof row.resource === 'string' ? row.resource : undefined,
      issue: String(row.issue ?? ''),
      recommendation: String(row.recommendation ?? ''),
      estimated_savings_usd_monthly: asNumber(row.estimated_savings_usd_monthly),
      estimated_savings:
        typeof row.estimated_savings === 'string' ? row.estimated_savings : undefined,
      risk: asRisk(row.risk),
      effort: String(row.effort ?? ''),
      impact: typeof row.impact === 'string' ? row.impact : undefined,
      implementation: String(row.implementation ?? ''),
      verification: String(row.verification ?? ''),
      data_citation: String(row.data_citation ?? ''),
      related_guardrail:
        typeof row.related_guardrail === 'string' ? row.related_guardrail : undefined,
      related_change: typeof row.related_change === 'string' ? row.related_change : undefined,
    })),
    additional_data_needed: additional.filter(isRecord).map((row) => ({
      data_type: String(row.data_type ?? ''),
      reason: String(row.reason ?? ''),
      how_to_get: String(row.how_to_get ?? ''),
    })),
  }
}
